<script lang="ts">
  /**
   * Self-service profile page. Lets the signed-in student manage their own:
   *   - profile photo (reuses the downscale + upload pipeline)
   *   - full name + nickname (nickname shows instead of the email)
   *   - preferred language
   *   - email address (with new-address verification + pending-state UI)
   *   - password (current password required)
   *   - E2EE identity status + recovery code (set up / restore)
   *   - read-only teacher contact address for the current room
   */
  import { onMount } from "svelte";
  import { push, link, querystring } from "svelte-spa-router";
  import {
    getMyProfileImage,
    setMyProfileImage,
    updateProfile,
    changePassword,
    changeEmail,
    getPendingEmail,
    getTeacherContact,
    e2eeStatus,
    ApiError,
  } from "../lib/api";
  import { session, patchUser } from "../lib/session.svelte";
  import { _, t, setLocale } from "../lib/i18n";
  import type { Locale, TeacherContact } from "@diy-avatar/shared/api";
  import Avatar from "../lib/Avatar.svelte";
  import WebcamCapture from "../lib/WebcamCapture.svelte";
  import EncryptionStatus from "../lib/EncryptionStatus.svelte";
  import QrScanner from "../lib/QrScanner.svelte";
  import {
    provisionKeyFromQrText,
    provisionKeyFromManualText,
  } from "../lib/e2eeProvision";
  import { renderProfileRenditions, type ProfileRenditions } from "../lib/image";
  import {
    setupRecovery,
    recoverWithCode,
    disableRecovery,
    hasRecovery,
    hasIdentity,
    isRecoveryNeeded,
    getMyPublicKey,
    rewrapIdentityForNewPassword,
    classifyRewrap,
  } from "../lib/e2eeSession";

  type Status = { kind: "idle" | "saving" | "ok" | "error"; msg?: string };

  // ---- identity / profile fields ----
  let fullName = "";
  let nickname = "";
  let language: Locale = "de";
  let profileImage: string | null = null;
  let identityStatus: Status = { kind: "idle" };

  // ---- photo ----
  let fileInput: HTMLInputElement | undefined;
  let stagedThumb: string | null = null;
  let stagedFull: string | null = null;
  let showCamera = false;
  let photoStatus: Status = { kind: "idle" };

  // ---- email change ----
  let newEmail = "";
  let emailPassword = "";
  let pendingEmail: string | null = null;
  let emailStatus: Status = { kind: "idle" };

  // ---- password change ----
  let currentPassword = "";
  let newPassword = "";
  let confirmPassword = "";
  let passwordStatus: Status = { kind: "idle" };

  // ---- E2EE ----
  let e2eeEnabled = false;
  let e2eeReady = false;
  let recoveryNeeded = false;
  let recoverySet = false;
  // Set when a self-service password change succeeded server-side but the E2EE
  // private key could NOT be re-wrapped under the new password (Part C: the
  // error used to be silently swallowed). Surfaced so the student restores via
  // recovery / re-adds the key instead of being quietly decoupled from E2EE.
  let e2eeRewrapError = false;
  // Decision 3: confirm dialog before a self-service password change. Shown only
  // when E2EE is enabled (the change re-secures the key). When the change would
  // DESTROY the key and no recovery exists, we instead block with a nudge.
  let pwConfirmOpen = false;
  let pwConfirmChecking = false;
  let publicKey: string | null = null;
  let recoveryCode: string | null = null;
  let recoveryStatus: Status = { kind: "idle" };
  let restoreCode = "";
  let restorePassword = "";
  let restoreStatus: Status = { kind: "idle" };

  // ---- E2EE key provisioning (Part B: scan QR / paste key) ----
  // Lets a parent on an E2EE-UNSUPPORTED browser (iPhone) add the classroom key
  // from this page — sealing+storing is pure libsodium + HTTP, no insertable
  // streams needed.
  let showScanner = false;
  let manualKey = "";
  let provisionStatus: Status = { kind: "idle" };

  // ---- teacher contact ----
  let teachers: TeacherContact[] = [];
  let teacherRoom: string | null = null;

  $: roomParam = new URLSearchParams($querystring ?? "").get("room") ?? "";

  onMount(async () => {
    if (!$session.user) {
      push("/");
      return;
    }
    fullName = $session.user.full_name ?? "";
    nickname = $session.user.display_name ?? "";
    language = $session.user.language ?? "de";

    void loadImage();
    void loadPending();
    void loadTeacher();
    void loadE2ee();
  });

  async function loadImage() {
    try {
      const res = await getMyProfileImage();
      profileImage = res.image;
    } catch {
      profileImage = null;
    }
  }

  async function loadPending() {
    try {
      const res = await getPendingEmail();
      pendingEmail = res.pending_email;
    } catch {
      pendingEmail = null;
    }
  }

  async function loadTeacher() {
    try {
      const res = await getTeacherContact(roomParam || undefined);
      teachers = res.teachers;
      teacherRoom = res.room;
    } catch {
      teachers = [];
    }
  }

  async function loadE2ee() {
    try {
      const st = await e2eeStatus();
      e2eeEnabled = st.enabled === true;
    } catch {
      e2eeEnabled = false;
    }
    if (!e2eeEnabled) return;
    e2eeReady = hasIdentity();
    recoveryNeeded = isRecoveryNeeded();
    publicKey = getMyPublicKey();
    recoverySet = await hasRecovery();
  }

  // ---- profile (name / nickname / language) ----
  async function saveProfile() {
    identityStatus = { kind: "saving" };
    try {
      const res = await updateProfile({
        full_name: fullName.trim() || null,
        display_name: nickname.trim() || null,
        language,
      });
      patchUser({
        full_name: res.full_name,
        display_name: res.display_name,
        language: res.language,
      });
      setLocale(res.language);
      identityStatus = { kind: "ok", msg: t("profilePage.saved") };
    } catch {
      identityStatus = { kind: "error", msg: t("profilePage.saveError") };
    }
  }

  // ---- photo ----
  function pickFile() {
    fileInput?.click();
  }

  async function onFile(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    try {
      const r = await renderProfileRenditions(file);
      stagedThumb = r.thumb;
      stagedFull = r.full;
      photoStatus = { kind: "idle" };
    } catch {
      photoStatus = { kind: "error", msg: t("profile.error") };
    }
  }

  function onCapture(e: CustomEvent<ProfileRenditions>) {
    stagedThumb = e.detail.thumb;
    stagedFull = e.detail.full;
    showCamera = false;
    photoStatus = { kind: "idle" };
  }

  async function savePhoto() {
    if (!stagedThumb) return;
    photoStatus = { kind: "saving" };
    try {
      const res = await setMyProfileImage(stagedThumb, stagedFull);
      profileImage = res.image ?? stagedThumb;
      stagedThumb = null;
      stagedFull = null;
      photoStatus = { kind: "ok", msg: t("profilePage.saved") };
    } catch (err) {
      photoStatus = {
        kind: "error",
        msg:
          err instanceof ApiError && err.status === 413
            ? t("profile.tooLarge")
            : t("profile.error"),
      };
    }
  }

  async function removePhoto() {
    photoStatus = { kind: "saving" };
    try {
      await setMyProfileImage(null);
      profileImage = null;
      stagedThumb = null;
      stagedFull = null;
      photoStatus = { kind: "ok", msg: t("profilePage.photoRemoved") };
    } catch {
      photoStatus = { kind: "error", msg: t("profile.error") };
    }
  }

  // ---- email ----
  async function submitEmail() {
    if (!newEmail || !emailPassword) {
      emailStatus = { kind: "error", msg: t("profilePage.required") };
      return;
    }
    emailStatus = { kind: "saving" };
    try {
      const res = await changeEmail({
        new_email: newEmail.trim(),
        current_password: emailPassword,
      });
      pendingEmail = res.pending_email;
      newEmail = "";
      emailPassword = "";
      emailStatus = { kind: "ok", msg: t("profilePage.email.sent") };
    } catch (err) {
      emailStatus = { kind: "error", msg: emailError(err) };
    }
  }

  function emailError(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.message === "wrong_password") return t("profilePage.wrongPassword");
      if (err.message === "email_taken") return t("profilePage.email.taken");
      if (err.message === "invalid_email") return t("profilePage.email.invalid");
    }
    return t("profilePage.saveError");
  }

  // ---- password ----
  // Step 1: validate, then (when E2EE is on) classify whether the change can
  // preserve the key. Decision 3: block when it would DESTROY the key and no
  // recovery exists; otherwise open the confirm dialog. Without E2EE, change
  // directly.
  async function submitPassword() {
    if (!currentPassword || !newPassword) {
      passwordStatus = { kind: "error", msg: t("profilePage.required") };
      return;
    }
    if (newPassword !== confirmPassword) {
      passwordStatus = { kind: "error", msg: t("profilePage.password.mismatch") };
      return;
    }
    if (!e2eeEnabled) {
      await doPasswordChange();
      return;
    }
    passwordStatus = { kind: "idle" };
    pwConfirmChecking = true;
    try {
      const cls = await classifyRewrap(currentPassword);
      recoverySet = await hasRecovery();
      if (cls === "destroy" && !recoverySet) {
        // Would orphan the key and there is no recovery key → block with a nudge
        // to create one first (decision 3 hard-require).
        passwordStatus = {
          kind: "error",
          msg: t("profilePage.password.needRecovery"),
        };
        return;
      }
    } catch {
      // If classification fails, fall through to the confirm dialog (which warns
      // the user a recovery key may be needed).
    } finally {
      pwConfirmChecking = false;
    }
    pwConfirmOpen = true;
  }

  function cancelPwConfirm() {
    pwConfirmOpen = false;
  }

  async function confirmPwChange() {
    pwConfirmOpen = false;
    await doPasswordChange();
  }

  // Step 2: perform the actual change (after confirm / when E2EE is off).
  async function doPasswordChange() {
    passwordStatus = { kind: "saving" };
    e2eeRewrapError = false;
    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Keep the E2EE identity usable: re-wrap the in-memory private key under
      // the new password. The password change has already succeeded server-side,
      // so a re-wrap failure does NOT roll it back — but it MUST be surfaced
      // (Part C) so the student knows the key needs recovery/re-add. Refresh the
      // recovery panel state so it is prominent when this happens.
      if (e2eeEnabled) {
        try {
          await rewrapIdentityForNewPassword(currentPassword, newPassword);
        } catch {
          e2eeRewrapError = true;
          recoveryNeeded = isRecoveryNeeded();
          recoverySet = await hasRecovery();
        }
      }
      currentPassword = "";
      newPassword = "";
      confirmPassword = "";
      passwordStatus = e2eeRewrapError
        ? { kind: "error", msg: t("profilePage.password.changedRewrapFailed") }
        : { kind: "ok", msg: t("profilePage.password.changed") };
    } catch (err) {
      const msg =
        err instanceof ApiError && err.message === "wrong_password"
          ? t("profilePage.wrongPassword")
          : err instanceof ApiError && err.message === "weak_password"
            ? t("profilePage.password.weak")
            : t("profilePage.saveError");
      passwordStatus = { kind: "error", msg };
    }
  }

  // ---- E2EE recovery ----
  async function doSetupRecovery() {
    recoveryStatus = { kind: "saving" };
    try {
      recoveryCode = await setupRecovery();
      recoverySet = true;
      recoveryStatus = { kind: "ok", msg: t("profilePage.e2ee.recoverySaved") };
    } catch {
      recoveryStatus = { kind: "error", msg: t("profilePage.e2ee.recoveryError") };
    }
  }

  async function doDisableRecovery() {
    recoveryStatus = { kind: "saving" };
    try {
      await disableRecovery();
      recoverySet = false;
      recoveryCode = null;
      recoveryStatus = { kind: "ok", msg: t("profilePage.e2ee.recoveryDisabled") };
    } catch {
      recoveryStatus = { kind: "error", msg: t("profilePage.e2ee.recoveryError") };
    }
  }

  async function doRestore() {
    if (!restoreCode || !restorePassword) {
      restoreStatus = { kind: "error", msg: t("profilePage.required") };
      return;
    }
    restoreStatus = { kind: "saving" };
    try {
      const ok = await recoverWithCode(restoreCode, restorePassword);
      if (ok) {
        e2eeReady = hasIdentity();
        recoveryNeeded = isRecoveryNeeded();
        publicKey = getMyPublicKey();
        restoreCode = "";
        restorePassword = "";
        restoreStatus = { kind: "ok", msg: t("profilePage.e2ee.restored") };
      } else {
        restoreStatus = { kind: "error", msg: t("profilePage.e2ee.restoreFailed") };
      }
    } catch {
      restoreStatus = { kind: "error", msg: t("profilePage.e2ee.restoreFailed") };
    }
  }

  // ---- E2EE key provisioning (scan / paste) ----
  function provisionMsg(reason: string): string {
    // Reuse the existing e2ee.scan.* strings for the shared failure reasons.
    return t(`e2ee.scan.${reason}`);
  }

  async function onProvisionScan(e: CustomEvent<string>) {
    provisionStatus = { kind: "saving" };
    const res = await provisionKeyFromQrText(e.detail);
    if (res.ok) {
      showScanner = false;
      provisionStatus = { kind: "ok", msg: t("e2ee.scan.stored") };
    } else {
      provisionStatus = { kind: "error", msg: provisionMsg(res.reason) };
    }
  }

  async function submitManualKey() {
    if (!manualKey.trim()) {
      provisionStatus = { kind: "error", msg: t("profilePage.required") };
      return;
    }
    provisionStatus = { kind: "saving" };
    const res = await provisionKeyFromManualText(manualKey);
    if (res.ok) {
      manualKey = "";
      provisionStatus = { kind: "ok", msg: t("e2ee.scan.stored") };
    } else {
      provisionStatus = { kind: "error", msg: provisionMsg(res.reason) };
    }
  }

  function fingerprint(key: string | null): string {
    if (!key) return "";
    return key.length > 16 ? `${key.slice(0, 8)}…${key.slice(-8)}` : key;
  }
</script>

<section class="profile">
  <header class="bar">
    <a class="back" href="/class" use:link data-testid="profile-back">
      {$_("profilePage.back")}
    </a>
    <h1>{$_("profilePage.title")}</h1>
  </header>

  <!-- Photo -->
  <div class="panel" data-testid="profile-photo">
    <h2>{$_("profilePage.photo.title")}</h2>
    <div class="photo-row">
      <Avatar
        image={stagedThumb ?? profileImage}
        name={nickname || $session.user?.email || ""}
        size={96}
        label={$_("profile.placeholderAlt")}
      />
      <div class="actions">
        <input
          bind:this={fileInput}
          type="file"
          accept="image/*"
          capture="user"
          class="hidden-file"
          data-testid="profile-photo-file"
          on:change={onFile}
        />
        <button type="button" class="btn" on:click={pickFile}>
          {$_("profile.choose")}
        </button>
        <button type="button" class="btn" on:click={() => (showCamera = true)}>
          {$_("profile.takePhoto")}
        </button>
        {#if stagedThumb}
          <button
            type="button"
            class="btn primary"
            data-testid="profile-photo-save"
            disabled={photoStatus.kind === "saving"}
            on:click={savePhoto}
          >
            {$_("profile.save")}
          </button>
        {/if}
        {#if profileImage && !stagedThumb}
          <button type="button" class="btn ghost" on:click={removePhoto}>
            {$_("profilePage.photo.remove")}
          </button>
        {/if}
      </div>
    </div>
    {#if photoStatus.msg}
      <p class="msg {photoStatus.kind}">{photoStatus.msg}</p>
    {/if}
  </div>

  <!-- Name / nickname / language -->
  <div class="panel" data-testid="profile-identity">
    <h2>{$_("profilePage.identity.title")}</h2>
    <label>
      {$_("profilePage.identity.fullName")}
      <input type="text" bind:value={fullName} data-testid="profile-fullname" />
    </label>
    <label>
      {$_("profilePage.identity.nickname")}
      <input type="text" bind:value={nickname} data-testid="profile-nickname" />
    </label>
    <p class="hint">{$_("profilePage.identity.nicknameHint")}</p>
    <label>
      {$_("profilePage.identity.language")}
      <select bind:value={language} data-testid="profile-language">
        <option value="de">Deutsch</option>
        <option value="en">English</option>
      </select>
    </label>
    <div class="actions">
      <button
        type="button"
        class="btn primary"
        data-testid="profile-save"
        disabled={identityStatus.kind === "saving"}
        on:click={saveProfile}
      >
        {$_("profile.save")}
      </button>
    </div>
    {#if identityStatus.msg}
      <p class="msg {identityStatus.kind}">{identityStatus.msg}</p>
    {/if}
  </div>

  <!-- Email -->
  <div class="panel" data-testid="profile-email">
    <h2>{$_("profilePage.email.title")}</h2>
    <p class="current">
      {$_("profilePage.email.current")}: <strong>{$session.user?.email}</strong>
    </p>
    {#if pendingEmail}
      <p class="pending" data-testid="profile-email-pending">
        {t("profilePage.email.pending", { email: pendingEmail })}
      </p>
    {/if}
    <label>
      {$_("profilePage.email.new")}
      <input type="email" bind:value={newEmail} data-testid="profile-email-new" />
    </label>
    <label>
      {$_("profilePage.currentPassword")}
      <input
        type="password"
        autocomplete="current-password"
        bind:value={emailPassword}
        data-testid="profile-email-password"
      />
    </label>
    <div class="actions">
      <button
        type="button"
        class="btn primary"
        data-testid="profile-email-submit"
        disabled={emailStatus.kind === "saving"}
        on:click={submitEmail}
      >
        {$_("profilePage.email.submit")}
      </button>
    </div>
    {#if emailStatus.msg}
      <p class="msg {emailStatus.kind}">{emailStatus.msg}</p>
    {/if}
  </div>

  <!-- Password -->
  <div class="panel" data-testid="profile-password">
    <h2>{$_("profilePage.password.title")}</h2>
    <label>
      {$_("profilePage.currentPassword")}
      <input
        type="password"
        autocomplete="current-password"
        bind:value={currentPassword}
        data-testid="profile-password-current"
      />
    </label>
    <label>
      {$_("profilePage.password.new")}
      <input
        type="password"
        autocomplete="new-password"
        bind:value={newPassword}
        data-testid="profile-password-new"
      />
    </label>
    <label>
      {$_("profilePage.password.confirm")}
      <input
        type="password"
        autocomplete="new-password"
        bind:value={confirmPassword}
        data-testid="profile-password-confirm"
      />
    </label>
    <div class="actions">
      <button
        type="button"
        class="btn primary"
        data-testid="profile-password-submit"
        disabled={passwordStatus.kind === "saving" || pwConfirmChecking}
        on:click={submitPassword}
      >
        {$_("profilePage.password.submit")}
      </button>
    </div>
    {#if passwordStatus.msg}
      <p class="msg {passwordStatus.kind}">{passwordStatus.msg}</p>
    {/if}
  </div>

  <!-- E2EE -->
  {#if e2eeEnabled}
    <div class="panel" data-testid="profile-e2ee">
      <h2>
        {$_("profilePage.e2ee.title")}
        <EncryptionStatus encrypted={e2eeReady} />
      </h2>
      <p class="hint">{$_("profilePage.e2ee.info")}</p>
      {#if recoveryNeeded}
        <p class="notice" data-testid="profile-recovery-needed">
          {$_("profilePage.e2ee.recoveryNeeded")}
        </p>
      {/if}
      {#if e2eeRewrapError}
        <p class="notice" data-testid="profile-rewrap-error">
          {$_("profilePage.e2ee.rewrapError")}
        </p>
      {/if}
      <p class="current">
        {$_("profilePage.e2ee.identity")}:
        <strong>
          {e2eeReady ? fingerprint(publicKey) : $_("profilePage.e2ee.noIdentity")}
        </strong>
      </p>

      <h3>{$_("e2ee.manual.title")}</h3>
      <p class="hint">{$_("e2ee.manual.info")}</p>
      {#if !e2eeReady}
        <p class="hint" data-testid="profile-provision-no-identity">
          {$_("e2ee.scan.noIdentity")}
        </p>
      {:else}
        <div class="actions">
          <button
            type="button"
            class="btn primary"
            data-testid="profile-add-key"
            on:click={() => {
              showScanner = true;
              provisionStatus = { kind: "idle" };
            }}
          >
            {$_("e2ee.manual.addKey")}
          </button>
        </div>
        {#if showScanner}
          <div data-testid="profile-scanner">
            <QrScanner
              on:scan={onProvisionScan}
              on:close={() => (showScanner = false)}
            />
          </div>
        {/if}
        <label>
          {$_("e2ee.manual.pasteLabel")}
          <textarea
            rows="3"
            bind:value={manualKey}
            data-testid="profile-manual-key"
            placeholder={$_("e2ee.manual.placeholder")}
          ></textarea>
        </label>
        <div class="actions">
          <button
            type="button"
            class="btn"
            data-testid="profile-manual-submit"
            disabled={provisionStatus.kind === "saving"}
            on:click={submitManualKey}
          >
            {$_("e2ee.manual.submit")}
          </button>
        </div>
        {#if provisionStatus.msg}
          <p class="msg {provisionStatus.kind}" data-testid="profile-provision-msg">
            {provisionStatus.msg}
          </p>
        {/if}
      {/if}

      <h3>{$_("profilePage.e2ee.recoveryTitle")}</h3>
      <p class="hint">{$_("profilePage.e2ee.recoveryInfo")}</p>
      {#if recoveryCode}
        <div class="recovery-code" data-testid="profile-recovery-code">
          <code>{recoveryCode}</code>
          <p class="warn">{$_("profilePage.e2ee.recoveryWarn")}</p>
        </div>
      {/if}
      <div class="actions">
        <button
          type="button"
          class="btn primary"
          data-testid="profile-recovery-setup"
          disabled={!e2eeReady || recoveryStatus.kind === "saving"}
          on:click={doSetupRecovery}
        >
          {recoverySet
            ? $_("profilePage.e2ee.recoveryRotate")
            : $_("profilePage.e2ee.recoverySetup")}
        </button>
        {#if recoverySet}
          <button
            type="button"
            class="btn ghost"
            on:click={doDisableRecovery}
            disabled={recoveryStatus.kind === "saving"}
          >
            {$_("profilePage.e2ee.recoveryDisable")}
          </button>
        {/if}
      </div>
      {#if recoveryStatus.msg}
        <p class="msg {recoveryStatus.kind}">{recoveryStatus.msg}</p>
      {/if}

      <h3>{$_("profilePage.e2ee.restoreTitle")}</h3>
      <p class="hint">{$_("profilePage.e2ee.restoreInfo")}</p>
      <label>
        {$_("profilePage.e2ee.restoreCode")}
        <input
          type="text"
          bind:value={restoreCode}
          data-testid="profile-restore-code"
        />
      </label>
      <label>
        {$_("profilePage.currentPassword")}
        <input
          type="password"
          autocomplete="current-password"
          bind:value={restorePassword}
          data-testid="profile-restore-password"
        />
      </label>
      <div class="actions">
        <button
          type="button"
          class="btn"
          data-testid="profile-restore-submit"
          disabled={restoreStatus.kind === "saving"}
          on:click={doRestore}
        >
          {$_("profilePage.e2ee.restoreSubmit")}
        </button>
      </div>
      {#if restoreStatus.msg}
        <p class="msg {restoreStatus.kind}">{restoreStatus.msg}</p>
      {/if}
    </div>
  {/if}

  <!-- Teacher contact -->
  <div class="panel" data-testid="profile-teacher">
    <h2>{$_("profilePage.teacher.title")}</h2>
    {#if teachers.length > 0}
      <p class="hint">
        {teacherRoom
          ? t("profilePage.teacher.forRoom", { room: teacherRoom })
          : $_("profilePage.teacher.info")}
      </p>
      <ul class="teacher-list">
        {#each teachers as teach (teach.email)}
          <li>
            {#if teach.name}<span class="tname">{teach.name}</span>{/if}
            <a href={`mailto:${teach.email}`}>{teach.email}</a>
          </li>
        {/each}
      </ul>
    {:else}
      <p class="hint">{$_("profilePage.teacher.none")}</p>
    {/if}
  </div>
</section>

{#if pwConfirmOpen}
  <div
    class="pw-dialog-backdrop"
    data-testid="pw-confirm-backdrop"
    role="dialog"
    aria-modal="true"
    aria-labelledby="pw-confirm-title"
  >
    <div class="pw-dialog">
      <h2 id="pw-confirm-title">{$_("profilePage.password.confirmTitle")}</h2>
      <p>{$_("profilePage.password.confirmBody")}</p>
      {#if !recoverySet}
        <p class="warn" data-testid="pw-confirm-no-recovery">
          {$_("profilePage.password.confirmNoRecovery")}
        </p>
      {/if}
      <div class="actions">
        <button
          type="button"
          class="btn"
          data-testid="pw-confirm-cancel"
          on:click={cancelPwConfirm}
        >
          {$_("profilePage.password.confirmCancel")}
        </button>
        <button
          type="button"
          class="btn primary"
          data-testid="pw-confirm-proceed"
          on:click={confirmPwChange}
        >
          {$_("profilePage.password.confirmProceed")}
        </button>
      </div>
    </div>
  </div>
{/if}

{#if showCamera}
  <WebcamCapture on:capture={onCapture} on:close={() => (showCamera = false)} />
{/if}

<style>
  .profile {
    max-width: 640px;
    margin: 0 auto;
    padding: 1rem;
    /* iOS: viewport-fit=cover renders content under the status bar, which would
       swallow the .bar back link. env() is 0 on non-notch devices, so max()
       keeps the original 1rem/2rem padding everywhere else. */
    padding-top: max(1rem, env(safe-area-inset-top));
    padding-bottom: max(2rem, env(safe-area-inset-bottom));
    display: grid;
    gap: 1rem;
    max-height: 100dvh;
    overflow-y: auto;
  }
  .bar {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  .bar h1 {
    margin: 0;
    font-size: 1.4rem;
  }
  .back {
    color: #6ee7b7;
    text-decoration: none;
    font-size: 0.9rem;
  }
  .back:hover {
    text-decoration: underline;
  }
  .panel {
    background: #111827;
    border: 1px solid #374151;
    border-radius: 12px;
    padding: 1.25rem;
    display: grid;
    gap: 0.6rem;
  }
  .panel h2 {
    margin: 0 0 0.25rem;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .panel h3 {
    margin: 0.75rem 0 0;
    font-size: 0.95rem;
    color: #e5e7eb;
  }
  label {
    display: grid;
    gap: 0.25rem;
    font-size: 0.85rem;
    color: #d1d5db;
  }
  input,
  select,
  textarea {
    padding: 0.55rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 8px;
    font-size: 1rem;
  }
  textarea {
    font-family: inherit;
    resize: vertical;
    word-break: break-all;
  }
  input:focus,
  select:focus,
  textarea:focus {
    outline: 2px solid #10b981;
    outline-offset: 1px;
  }
  .photo-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .hidden-file {
    display: none;
  }
  .actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .btn {
    appearance: none;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: #374151;
    color: #f3f4f6;
    border-radius: 0.6rem;
    padding: 0.55rem 1rem;
    font-size: 0.95rem;
    cursor: pointer;
  }
  .btn.ghost {
    background: transparent;
  }
  .btn.primary {
    background: #10b981;
    color: #04221a;
    border-color: #10b981;
    font-weight: 600;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .hint {
    margin: 0;
    color: #9ca3af;
    font-size: 0.82rem;
  }
  .current {
    margin: 0;
    color: #d1d5db;
    font-size: 0.9rem;
  }
  .pending {
    margin: 0;
    color: #fcd34d;
    font-size: 0.85rem;
  }
  .notice {
    margin: 0;
    padding: 0.6rem 0.75rem;
    background: rgba(252, 211, 77, 0.12);
    border: 1px solid #b45309;
    border-radius: 8px;
    color: #fcd34d;
    font-size: 0.85rem;
  }
  .msg {
    margin: 0;
    font-size: 0.85rem;
  }
  .msg.ok {
    color: #6ee7b7;
  }
  .msg.error {
    color: #fca5a5;
  }
  .recovery-code {
    background: #0b1220;
    border: 1px dashed #4b5563;
    border-radius: 8px;
    padding: 0.75rem;
    text-align: center;
  }
  .recovery-code code {
    font-size: 1.1rem;
    letter-spacing: 1px;
    color: #6ee7b7;
    word-break: break-all;
  }
  .warn {
    margin: 0.5rem 0 0;
    color: #fcd34d;
    font-size: 0.8rem;
  }
  .teacher-list {
    margin: 0;
    padding-left: 1.1rem;
    display: grid;
    gap: 0.3rem;
  }
  .teacher-list a {
    color: #6ee7b7;
  }
  .tname {
    color: #d1d5db;
    margin-right: 0.4rem;
  }
  .pw-dialog-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: grid;
    place-items: center;
    padding: 1rem;
    /* Keep a tall dialog clear of the status bar / home indicator. */
    padding-top: max(1rem, env(safe-area-inset-top));
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
    z-index: 50;
  }
  .pw-dialog {
    width: 100%;
    max-width: 400px;
    background: #111827;
    border: 1px solid #374151;
    border-radius: 12px;
    padding: 1.25rem;
    display: grid;
    gap: 0.75rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  }
  .pw-dialog h2 {
    margin: 0;
    font-size: 1.1rem;
  }
  .pw-dialog p {
    margin: 0;
    color: #d1d5db;
    font-size: 0.9rem;
  }
  .pw-dialog .warn {
    color: #fcd34d;
  }
</style>
